import { Nullable } from '@/types/utility.type';

export const getByPath = (
  obj: Record<string, any>,
  path: string
): any | undefined => {
  const paths = path.split('.');
  let current: any = obj;

  for (const segment of paths) {
    if (
      !Object.prototype.hasOwnProperty.call(current, segment) ||
      segment === '__proto__' ||
      segment === 'constructor' ||
      segment === 'prototype'
    ) {
      return undefined;
    }

    current = current[segment];
  }

  return current;
};

export const mapEmptyToNull = <T extends Record<string, any>>(
  obj: T
): Nullable<T> => {
  const res: { [key: string]: any } = {};
  for (const key in obj) {
    if (typeof obj[key] === 'string') {
      res[key] = obj[key].trim().length ? obj[key] : null;
    } else if (
      typeof obj[key] === 'object' &&
      !Array.isArray(obj[key]) &&
      obj[key] != null
    ) {
      res[key] = mapEmptyToNull(obj[key]);
    } else {
      res[key] = obj[key];
    }
  }

  return res as Nullable<T>;
};

export const removeEmpty = <T extends Record<string, any>>(
  obj: T
): Partial<Required<T>> =>
  Object.keys(obj)
    .filter((k) => obj[k] != null)
    .reduce((a, k) => ({ ...a, [k]: obj[k] }), {});

export const removeUndefined = <T extends Record<string, any>>(
  obj: T
): Partial<T> =>
  Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .reduce((a, k) => ({ ...a, [k]: obj[k] }), {});

export const pick = <T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
) =>
  Object.fromEntries(
    keys.filter((key) => key in obj).map((key) => [key, obj[key]])
  ) as Pick<T, K>;

export const objectDiff = <
  PT extends Record<string, any>,
  RT extends Record<string, any>
>(
  primaryObj: PT,
  referenceObj: RT
): Partial<PT> => {
  const res: Partial<PT> = {};
  for (const key in primaryObj) {
    if (
      key in referenceObj &&
      JSON.stringify(primaryObj[key]) !==
        JSON.stringify(referenceObj[key as keyof RT])
    ) {
      res[key] = primaryObj[key];
    }
  }

  return res;
};

export const isObjectEmpty = (obj: object): boolean => !Object.keys(obj).length;

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const multiplyArray = <T>(arr: T[], multiplyFactor: number = 2): T[] =>
  Array.from({ length: multiplyFactor }, () => arr).flat();
